import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box, Typography, Avatar, Button, Chip, useMediaQuery, CircularProgress, Snackbar, Alert, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CommandBar from '../components/domain/CommandBar';
import SegmentedControl from '../components/domain/SegmentedControl';
import LeaderboardRow from '../components/domain/LeaderboardRow';
import ActivityFeed from '../components/domain/ActivityFeed';
import SimpleLineChart from '../components/domain/SimpleLineChart';
import ProgressArc from '../components/domain/ProgressArc';
import api from '../api/client';

const TABS = [
  { value: 'overview', label: 'Przegląd' },
  { value: 'participants', label: 'Uczestnicy' },
  { value: 'activity', label: 'Aktywność' },
];

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysUntil(iso) {
  if (!iso) return 0;
  const start = new Date(iso);
  const now = new Date();
  if (isNaN(start.getTime())) return 0;
  const diff = Math.ceil((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

function parseJsonField(value, fallback = null) {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

// Statusy pochodzą z automatycznej maszyny stanów w campaign-service:
// draft -> scheduled -> registration_open -> active -> wind_down -> completed
// (plus paused/cancelled jako akcje ręczne). Musi być zsynchronizowane z
// deriveFromWindows() w iac/cloudflare/workers/campaign-service/src/index.ts.
const STATUS_META = {
  draft: { label: 'Draft', color: 'default' },
  scheduled: { label: 'Zaplanowana', color: 'info' },
  registration_open: { label: 'W trakcie zapisów', color: 'info' },
  active: { label: 'Aktywna', color: 'success' },
  wind_down: { label: 'Rozliczenie', color: 'warning' },
  paused: { label: 'Wstrzymana', color: 'warning' },
  completed: { label: 'Zakończona', color: 'default' },
  cancelled: { label: 'Odwołana', color: 'error' },
};

function buildCampaignView(data) {
  const rules = parseJsonField(data.rules, {});
  const rewards = parseJsonField(data.rewards, []);
  const charity = parseJsonField(data.charity, {});
  const communication = parseJsonField(data.communication, {});
  const startDate = formatDate(data.start_date);
  const endDate = formatDate(data.end_date);
  const dus = daysUntil(data.start_date);

  const isScheduled = data.status === 'scheduled' || data.status === 'draft';
  const isStepsChallenge = rules.challengeType === 'steps';
  const statusMeta = STATUS_META[data.status] || STATUS_META.draft;

  return {
    ...data,
    isStepsChallenge,
    goalValue: rules.goalValue || 0,
    title: data.title || 'Kampania bez nazwy',
    statusLabel: statusMeta.label,
    statusColor: statusMeta.color,
    startDate,
    endDate,
    daysUntilStart: dus,
    progress: charity.currentAmount && charity.goalAmount ? Math.round((charity.currentAmount / charity.goalAmount) * 100) : 0,
    totalSteps: charity.currentAmount ? charity.currentAmount.toLocaleString('pl-PL') : '0',
    goalSteps: charity.goalAmount ? charity.goalAmount.toLocaleString('pl-PL') : '0',
    type: data.template_id || 'Kampania',
    invitedCount: data.invitedCount || 0,
    confirmedCount: data.confirmedCount || 0,
    integrations: data.integrations || communication.channels || [],
    rewards: Array.isArray(rewards) && rewards.length ? rewards.map((r) => `${r.place}. ${r.prize}`).join(', ') : 'Nie skonfigurowano',
    communication: communication.channels ? communication.channels.join(', ') : (data.communication ? 'Skonfigurowano' : 'Nie skonfigurowano'),
    rules: rules.disciplines ? `Dyscypliny: ${rules.disciplines.join(', ')}` : 'Podstawowe zasady',
    goalStepsRaw: rules.dailyLimit || 0,
    checklist: [
      { label: 'Szablon kampanii', done: true },
      { label: 'Zasady i harmonogram', done: !!rules.disciplines },
      { label: 'Uczestnicy (min. 20)', done: (data.confirmedCount || 0) >= 20 },
      { label: 'Komunikacja i integracje', done: !!communication.channels },
      { label: 'Nagrody', done: Array.isArray(rewards) && rewards.length > 0 },
      { label: 'Podgląd i publikacja', done: data.status !== 'draft' },
    ],
    isScheduled,
  };
}

function CampaignDetail() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState('overview');
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get(`/api/campaigns/${id}`).catch((err) => ({ error: err.body?.error || err.message || 'fetch failed' })),
      api.get(`/api/leaderboard/${id}`).catch(() => null),
    ]).then(([campaignData, leaderboardData]) => {
      if (campaignData && !campaignData.error) {
        setCampaign(campaignData);
      } else {
        setCampaign(null);
        setError(campaignData?.error || 'Nie udało się pobrać kampanii');
      }
      const entries = Array.isArray(leaderboardData) ? leaderboardData : (leaderboardData?.results || []);
      setLeaderboard(entries);
      setLoading(false);
    });
  }, [id]);

  const CAMPAIGN = useMemo(() => {
    const view = campaign ? buildCampaignView(campaign) : null;
    if (view && view.isStepsChallenge) {
      const totalStepsRaw = leaderboard.reduce((sum, p) => sum + (Number(p.total_steps) || 0), 0);
      const goalStepsRaw = (view.goalValue || 0) * Math.max(leaderboard.length, 1);
      view.totalSteps = totalStepsRaw.toLocaleString('pl-PL');
      view.goalSteps = goalStepsRaw ? goalStepsRaw.toLocaleString('pl-PL') : '0';
      view.progress = goalStepsRaw ? Math.min(100, Math.round((totalStepsRaw / goalStepsRaw) * 100)) : 0;
    }
    return view;
  }, [campaign, leaderboard]);

  const handleStatusChange = async (newStatus) => {
    setActionLoading(true);
    try {
      await api.put(`/api/campaigns/${id}`, { status: newStatus });
      setCampaign((prev) => (prev ? { ...prev, status: newStatus } : null));
      setSnackbar({ open: true, message: `Status zmieniony na ${newStatus}`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = () => handleStatusChange('active');
  const handleEnd = () => handleStatusChange('completed');
  const handlePause = () => handleStatusChange('paused');

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await api.del(`/api/campaigns/${id}`);
      setDeleteDialog(false);
      setSnackbar({ open: true, message: 'Kampania usunięta', severity: 'success' });
      setTimeout(() => navigate('/campaigns'), 1200);
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleGeneratePoster = async () => {
    setActionLoading(true);
    try {
      const res = await api.getBlob(`/api/poster/${id}`);
      const url = URL.createObjectURL(res);
      window.open(url, '_blank');
      setSnackbar({ open: true, message: 'Plakat wygenerowany', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd generowania plakatu: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDuplicate = async () => {
    if (!campaign) return;
    setActionLoading(true);
    try {
      const { data } = await api.post('/api/campaigns', {
        title: `${campaign.title} (kopia)`,
        startDate: campaign.start_date || new Date().toISOString(),
        endDate: campaign.end_date || new Date().toISOString(),
        rules: typeof campaign.rules === 'string' ? JSON.parse(campaign.rules) : (campaign.rules || {}),
        rewards: typeof campaign.rewards === 'string' ? JSON.parse(campaign.rewards) : (campaign.rewards || []),
      });
      setSnackbar({ open: true, message: 'Kampania skopiowana', severity: 'success' });
      navigate(`/campaigns/${data.id}`);
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !CAMPAIGN) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 2 }}>
        <Typography variant="h6" color="error">Błąd ładowania kampanii</Typography>
        <Typography variant="body2" color="text.secondary">{error || 'Kampania nie została znaleziona'}</Typography>
        <Button variant="outlined" onClick={() => navigate('/campaigns')} sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}>
          Wróć do listy
        </Button>
      </Box>
    );
  }

  const isScheduled = CAMPAIGN.isScheduled;

  // ─── SCHEDULED VIEW ─────────────────────────────────────────────
  if (isScheduled) {
    const completedSteps = CAMPAIGN.checklist?.filter((s) => s.done).length ?? 0;
    const totalSteps = CAMPAIGN.checklist?.length ?? 0;
    const readyToPublish = completedSteps === totalSteps;

    return (
      <Box>
        <CommandBar
          statusLabel={CAMPAIGN.statusLabel || 'Zaplanowana'}
          statusColor={CAMPAIGN.statusColor || 'info'}
          primaryAction={readyToPublish ? 'Opublikuj' : 'Edytuj'}
          onPrimaryAction={readyToPublish ? handlePublish : () => navigate(`/campaigns/${id}/landing`)}
          onBack={() => navigate('/campaigns')}
          secondaryActions={[
            { label: 'Strona zaproszeniowa', onClick: () => navigate(`/campaigns/${id}/landing`) },
            { label: 'Podgląd pracownika', onClick: () => navigate(`/campaigns/${id}/preview`) },
            { label: 'Generuj plakat', onClick: handleGeneratePoster },
            { label: 'Duplikuj', onClick: handleDuplicate },
            { label: 'Usuń', onClick: () => setDeleteDialog(true) },
          ]}
        />

        {/* Title + dates */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{ fontFamily: theme.typography.h1.fontFamily, fontSize: { xs: '1.5rem', md: '2rem' }, mb: 0.5 }}
          >
            {CAMPAIGN.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {CAMPAIGN.startDate} – {CAMPAIGN.endDate}
          </Typography>
        </Box>

        {/* Countdown hero */}
        <Box
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: `${theme.tokens?.primitive?.radius?.hero ?? 30}px`,
            bgcolor: theme.tokens?.semantic?.surface?.brandTint ?? 'primary.50',
            border: `1px solid ${theme.palette.divider}`,
            mb: 4,
            textAlign: 'center',
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
          <Typography
            variant="h2"
            sx={{
              fontFamily: theme.typography.h1.fontFamily,
              fontWeight: 800,
              fontSize: { xs: '2.5rem', sm: '3.5rem' },
              lineHeight: 1,
              color: 'primary.main',
            }}
          >
            {CAMPAIGN.daysUntilStart}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {CAMPAIGN.daysUntilStart === 1 ? 'dzień do startu' : 'dni do startu'}
          </Typography>
        </Box>

        {/* Info grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 4,
          }}
        >
          {[
            { label: 'Typ', value: CAMPAIGN.type },
            { label: 'Zaproszeni', value: `${CAMPAIGN.invitedCount} pracowników` },
            { label: 'Potwierdzili udział', value: `${CAMPAIGN.confirmedCount} osób` },
            { label: 'Integracje', value: CAMPAIGN.integrations?.length ? CAMPAIGN.integrations.join(', ') : 'Brak' },
            { label: 'Zasady', value: CAMPAIGN.rules },
            { label: 'Nagrody', value: CAMPAIGN.rewards },
            { label: 'Komunikacja', value: CAMPAIGN.communication },
            { label: 'Cel', value: CAMPAIGN.goalSteps },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                p: 2,
                borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px`,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Checklist */}
        <Box
          sx={{
            p: 3,
            borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            mb: 4,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Gotowość do startu
            </Typography>
            <Chip
              label={`${completedSteps}/${totalSteps}`}
              size="small"
              color={readyToPublish ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>

          {/* Progress bar */}
          <Box sx={{ height: 6, borderRadius: 3, bgcolor: theme.palette.action.selected, mb: 2.5, overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${(completedSteps / totalSteps) * 100}%`,
                bgcolor: readyToPublish ? 'success.main' : 'primary.main',
                borderRadius: 3,
                transition: 'width 420ms ease',
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {CAMPAIGN.checklist.map((step, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {step.done ? (
                  <CheckCircleOutlineIcon fontSize="small" color="success" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: step.done ? 500 : 600,
                    color: step.done ? 'text.secondary' : 'text.primary',
                    textDecoration: step.done ? 'line-through' : 'none',
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<EditOutlinedIcon />}
            onClick={() => navigate(`/campaigns/${id}/landing`)}
            sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            Edytuj kampanię
          </Button>
          <Button
            variant="outlined"
            startIcon={<SendOutlinedIcon />}
            onClick={() => navigate(`/campaigns/${id}/landing`)}
            sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            Wyślij zaproszenia
          </Button>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/employees')}
            sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            Dodaj uczestników
          </Button>
        </Box>

        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Usunąć kampanię?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tej operacji nie można cofnąć. Wszystkie dane kampanii, w tym landing, zostaną usunięte.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} disabled={actionLoading}>Anuluj</Button>
            <Button onClick={handleDelete} color="error" disabled={actionLoading}>
              {actionLoading ? 'Usuwanie…' : 'Usuń'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ─── ACTIVE VIEW ────────────────────────────────────────────────
  return (
    <Box>
      <CommandBar
        statusLabel={CAMPAIGN.statusLabel || 'Aktywna'}
        statusColor={CAMPAIGN.statusColor || 'success'}
        primaryAction={CAMPAIGN.status === 'paused' ? 'Wznów' : 'Wstrzymaj'}
        onPrimaryAction={CAMPAIGN.status === 'paused' ? handlePublish : handlePause}
        onBack={() => navigate('/campaigns')}
        secondaryActions={[
          { label: 'Strona zaproszeniowa', onClick: () => navigate(`/campaigns/${id}/landing`) },
          { label: 'Podgląd pracownika', onClick: () => navigate(`/campaigns/${id}/preview`) },
          { label: 'Generuj plakat', onClick: handleGeneratePoster },
          { label: 'Edytuj', onClick: () => navigate(`/campaigns/${id}/landing`) },
          { label: 'Zakończ', onClick: handleEnd },
          { label: 'Duplikuj', onClick: handleDuplicate },
          { label: 'Usuń', onClick: () => setDeleteDialog(true) },
        ]}
      />

      {/* Title + dates */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontFamily: theme.typography.h1.fontFamily, fontSize: { xs: '1.5rem', md: '2rem' }, mb: 0.5 }}
        >
          {CAMPAIGN.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {CAMPAIGN.startDate} – {CAMPAIGN.endDate}
        </Typography>
      </Box>

      {/* Hero result strip — 3 kluczowe liczby */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4, alignItems: 'center' }}>
        {[
          { label: 'Zaproszeni', value: CAMPAIGN.invitedCount },
          { label: 'Aktywni', value: leaderboard.length || CAMPAIGN.confirmedCount },
          { label: 'Cel', value: CAMPAIGN.goalSteps },
        ].map((m) => (
          <Box key={m.label}>
            <Typography
              variant="h4"
              sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1, fontSize: { xs: '1.75rem', md: '2.25rem' } }}
            >
              {m.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {m.label}
            </Typography>
          </Box>
        ))}
        <ProgressArc value={CAMPAIGN.progress} label={`${CAMPAIGN.progress}%`} sublabel="celu" size={isMobile ? 64 : 80} />
      </Box>

      {/* Segmented Control */}
      <Box sx={{ mb: 3 }}>
        <SegmentedControl options={TABS} value={activeTab} onChange={setActiveTab} />
      </Box>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <Box>
          <Box
            sx={{
              p: 3,
              borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              mb: 3,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
              Trend uczestnictwa
            </Typography>
            <SimpleLineChart data={[]} height={140} label="Aktywni uczestnicy w czasie" />
          </Box>

          <Box
            sx={{
              p: 3,
              borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              mb: 3,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
              Postęp kampanii
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h5" sx={{ fontFamily: theme.typography.h1.fontFamily }}>
                  {CAMPAIGN.totalSteps}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  z {CAMPAIGN.goalSteps} kroków
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupOutlinedIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{CAMPAIGN.confirmedCount} aktywnych</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon fontSize="small" color="success" />
                <Typography variant="body2" color="text.secondary">{CAMPAIGN.progress}% celu</Typography>
              </Box>
            </Box>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => window.open(`${import.meta.env.VITE_API_URL}/api/reports/export/${id}`, '_blank')}
            sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            Eksportuj raport
          </Button>
        </Box>
      )}

      {activeTab === 'participants' && (
        <Box
          sx={{
            borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Ranking ({leaderboard.length} uczestników)
            </Typography>
            <Button size="small" startIcon={<PersonAddIcon />} onClick={() => navigate('/employees')} sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}>
              Zaproś
            </Button>
          </Box>
          <Box sx={{ p: 1 }}>
            {leaderboard.length > 0 ? leaderboard.map((p, idx) => (
              <LeaderboardRow
                key={p.user_id}
                position={idx + 1}
                name={p.display_name}
                score={CAMPAIGN.isStepsChallenge ? (p.total_steps ?? 0) : p.total_points}
                scoreLabel={CAMPAIGN.isStepsChallenge ? 'kroków' : 'pkt'}
                trend={0}
                isCurrentUser={p.user_id === 'user_hr_anna'}
              />
            )) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {CAMPAIGN.isStepsChallenge
                    ? 'Nikt jeszcze nie dołączył do wyzwania (kliknięcie „Weź udział” w aplikacji).'
                    : 'Brak uczestników'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {activeTab === 'activity' && (
        <Box
          sx={{
            borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            p: 1,
          }}
        >
          <ActivityFeed items={[]} />
        </Box>
      )}

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Usunąć kampanię?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tej operacji nie można cofnąć. Wszystkie dane kampanii, w tym landing, zostaną usunięte.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={actionLoading}>Anuluj</Button>
          <Button onClick={handleDelete} color="error" disabled={actionLoading}>
            {actionLoading ? 'Usuwanie…' : 'Usuń'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CampaignDetail;
